import { createSlice } from '@reduxjs/toolkit'
import { mockInventoryData } from '../components/InventoryItems/utils/mockInventoryData'

const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    items: [
      {
        id: 1,
        name: 'Paracetamol 500mg',
        category: 'Medication',
        currentStock: 1567,
        minimumStock: 200,
        unitPrice: 16.5,
        expiryDate: '2024-12-15'
      },
      {
        id: 2,
        name: 'Amoxicillin 250mg',
        category: 'Medication',
        currentStock: 500,
        minimumStock: 150,
        unitPrice: 24.75,
        expiryDate: '2024-06-30'
      },
      {
        id: 3,
        name: 'Vitamin C 1000mg',
        category: 'Medication',
        currentStock: 1125,
        minimumStock: 100,
        unitPrice: 12.99,
        expiryDate: '2025-03-15'
      },
      {
        id: 4,
        name: 'Surgical Gloves',
        category: 'Consumables',
        currentStock: 850,
        minimumStock: 200,
        unitPrice: 8.75,
        expiryDate: '2024-08-20'
      },
      {
        id: 5,
        name: 'Face Masks',
        category: 'PPE',
        currentStock: 600,
        minimumStock: 500,
        unitPrice: 5.99,
        expiryDate: '2024-10-25'
      },
      {
        id: 6,
        name: 'Blood Pressure Monitor',
        category: 'Equipment',
        currentStock: 45,
        minimumStock: 10,
        unitPrice: 129.99,
        expiryDate: '2028-01-01'
      },
      {
        id: 7,
        name: 'Thermometer',
        category: 'Equipment',
        currentStock: 60,
        minimumStock: 15,
        unitPrice: 24.5,
        expiryDate: '2027-06-15'
      },
      {
        id: 8,
        name: 'Disposable Syringes',
        category: 'Consumables',
        currentStock: 1475,
        minimumStock: 300,
        unitPrice: 0.75,
        expiryDate: '2024-09-30'
      },
      {
        id: 9,
        name: 'Bandages',
        category: 'Consumables',
        currentStock: 800,
        minimumStock: 200,
        unitPrice: 3.25,
        expiryDate: '2025-05-20'
      },
      {
        id: 10,
        name: 'Antibiotic Ointment',
        category: 'Medication',
        currentStock: 350,
        minimumStock: 75,
        unitPrice: 8.99,
        expiryDate: '2024-07-15'
      },
      {
        id: 11,
        name: 'Protective Goggles',
        category: 'PPE',
        currentStock: 230,
        minimumStock: 50,
        unitPrice: 12.99,
        expiryDate: '2026-04-10'
      },
      {
        id: 12,
        name: 'Digital Stethoscope',
        category: 'Equipment',
        currentStock: 15,
        minimumStock: 5,
        unitPrice: 199.99,
        expiryDate: '2030-01-01'
      },
      {
        id: 13,
        name: 'Surgical Masks',
        category: 'PPE',
        currentStock: 400,
        minimumStock: 200,
        unitPrice: 0.99,
        expiryDate: '2024-11-30'
      },
      {
        id: 14,
        name: 'Ibuprofen 200mg',
        category: 'Medication',
        currentStock: 725,
        minimumStock: 150,
        unitPrice: 14.75,
        expiryDate: '2024-10-15'
      },
      {
        id: 15,
        name: 'Surgical Masks N95',
        category: 'PPE',
        currentStock: 15,
        minimumStock: 50,
        unitPrice: 35.99,
        expiryDate: '2024-09-15'
      },
      {
        id: 16,
        name: 'Insulin 100ml',
        category: 'Medication',
        currentStock: 5,
        minimumStock: 20,
        unitPrice: 149.5,
        expiryDate: '2024-08-10'
      },
      {
        id: 17,
        name: 'Examination Gloves',
        category: 'Consumables',
        currentStock: 100,
        minimumStock: 300,
        unitPrice: 2.5,
        expiryDate: '2025-01-30'
      },
      {
        id: 18,
        name: 'Blood Testing Strips',
        category: 'Consumables',
        currentStock: 45,
        minimumStock: 100,
        unitPrice: 8.75,
        expiryDate: '2024-11-05'
      },
      {
        id: 19,
        name: 'Sterile Needles',
        category: 'Consumables',
        currentStock: 180,
        minimumStock: 200,
        unitPrice: 1.25,
        expiryDate: '2025-03-15'
      }
    ],
    loading: false,
    error: null,
    selectedItem: null,
    filteredItems: mockInventoryData,
    searchTerm: '',
    categoryFilter: null,
    departmentFilter: null,
    stockStatusFilter: null,
    expiryStatusFilter: null
  },
  reducers: {
    // Basic CRUD operations
    setItems: (state, action) => {
      state.items = action.payload
      state.filteredItems = action.payload
    },
    addItem: (state, action) => {
      state.items.push(action.payload)
      state.filteredItems = [...state.items]
    },
    updateItem: (state, action) => {
      const index = state.items.findIndex((item) => item.id === action.payload.id)
      if (index !== -1) {
        state.items[index] = action.payload
        state.filteredItems = [...state.items]

        // If the updated item is the selected item, update that too
        if (state.selectedItem && state.selectedItem.id === action.payload.id) {
          state.selectedItem = action.payload
        }
      }
    },
    deleteItem: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload)
      state.filteredItems = [...state.items]

      // If the deleted item is the selected item, clear selection
      if (state.selectedItem && state.selectedItem.id === action.payload) {
        state.selectedItem = null
      }
    },

    // Selection state
    selectItem: (state, action) => {
      state.selectedItem = action.payload
    },
    clearSelection: (state) => {
      state.selectedItem = null
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },

    // Filtering and search
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
      state.filteredItems = filterItems(state.items, state)
    },
    setCategoryFilter: (state, action) => {
      state.categoryFilter = action.payload
      state.filteredItems = filterItems(state.items, state)
    },
    setDepartmentFilter: (state, action) => {
      state.departmentFilter = action.payload
      state.filteredItems = filterItems(state.items, state)
    },
    setStockStatusFilter: (state, action) => {
      state.stockStatusFilter = action.payload
      state.filteredItems = filterItems(state.items, state)
    },
    setExpiryStatusFilter: (state, action) => {
      state.expiryStatusFilter = action.payload
      state.filteredItems = filterItems(state.items, state)
    },
    clearFilters: (state) => {
      state.searchTerm = ''
      state.categoryFilter = null
      state.departmentFilter = null
      state.stockStatusFilter = null
      state.expiryStatusFilter = null
      state.filteredItems = [...state.items]
    }
  }
})

// Helper function for filtering items based on multiple criteria
const filterItems = (items, state) => {
  const { searchTerm, categoryFilter, departmentFilter, stockStatusFilter, expiryStatusFilter } = state

  return items.filter((item) => {
    // Search term filter (across multiple fields)
    const matchesSearch =
      !searchTerm ||
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.nappiCode && item.nappiCode.includes(searchTerm)) ||
      (item.lotNumber && item.lotNumber.toLowerCase().includes(searchTerm.toLowerCase()))

    // Category filter
    const matchesCategory = !categoryFilter || item.category === categoryFilter

    // Department filter
    const matchesDepartment = !departmentFilter || item.department === departmentFilter

    // Stock status filter
    const matchesStockStatus = !stockStatusFilter || getItemStockStatus(item) === stockStatusFilter

    // Expiry status filter
    const matchesExpiryStatus = !expiryStatusFilter || getItemExpiryStatus(item) === expiryStatusFilter

    return matchesSearch && matchesCategory && matchesDepartment && matchesStockStatus && matchesExpiryStatus
  })
}

// Helper functions for status determination
const getItemStockStatus = (item) => {
  if (item.quantityOnHand === 0) {
    return 'Out of Stock'
  }

  if (item.quantityOnHand < item.minStockLevel) {
    return 'Low Stock'
  }

  if (item.quantityOnHand > item.maxStockLevel) {
    return 'Over Stock'
  }

  return 'Healthy'
}

const getItemExpiryStatus = (item) => {
  if (!item.expiryDate) {
    return 'No Expiry'
  }

  const today = new Date()
  const expiryDate = new Date(item.expiryDate)
  const daysToExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24))

  if (daysToExpiry < 0) {
    return 'Expired'
  }

  if (daysToExpiry <= 30) {
    return 'Critical Expiry'
  }

  if (daysToExpiry <= 90) {
    return 'Expiring Soon'
  }

  return 'Valid'
}

export const {
  setItems,
  addItem,
  updateItem,
  deleteItem,
  selectItem,
  clearSelection,
  setLoading,
  setError,
  setSearchTerm,
  setCategoryFilter,
  setDepartmentFilter,
  setStockStatusFilter,
  setExpiryStatusFilter,
  clearFilters
} = inventorySlice.actions

export default inventorySlice.reducer
